/**
 * Capacitor Platform Utilities
 * Handles native platform detection and features
 */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';

// Platform detection
export const isNative = Capacitor.isNativePlatform();
export const isAndroid = Capacitor.getPlatform() === 'android';
export const isIOS = Capacitor.getPlatform() === 'ios';
export const isWeb = Capacitor.getPlatform() === 'web';

// Initialize native features
export const initializeApp = async () => {
  if (!isNative) return;

  try {
    // Configure status bar
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#0f0f23' });

    // Hide splash screen after app is ready
    await SplashScreen.hide();

    // Setup back button handler for Android
    if (isAndroid) {
      App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        } else {
          App.exitApp();
        }
      });
    }

    // Setup keyboard listeners
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.setProperty('--keyboard-height', '0px');
    });

    console.log('Native app initialized successfully');
  } catch (error) {
    console.error('Error initializing native features:', error);
  }
};

// Haptic feedback
export const hapticFeedback = async (style = 'light') => {
  if (!isNative) return;

  try {
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    await Haptics.impact({ style: impactStyle[style] || ImpactStyle.Light });
  } catch (error) {
    console.error('Haptic feedback error:', error);
  }
};

// Vibrate
export const vibrate = async () => {
  if (!isNative) return;
  
  try {
    await Haptics.vibrate();
  } catch (error) {
    console.error('Vibrate error:', error);
  }
};

// Hide keyboard
export const hideKeyboard = async () => {
  if (!isNative) return;
  
  try {
    await Keyboard.hide();
  } catch (error) {
    console.error('Hide keyboard error:', error);
  }
};

// Get app info
export const getAppInfo = async () => {
  if (!isNative) {
    return {
      name: 'Stronger Bot',
      version: '1.0.0',
      build: '1',
      platform: 'web'
    };
  }

  try {
    const info = await App.getInfo();
    return {
      ...info,
      platform: Capacitor.getPlatform()
    };
  } catch (error) {
    console.error('Get app info error:', error);
    return null;
  }
};

// Check for app updates (placeholder for future implementation)
export const checkForUpdates = async () => {
  // This would integrate with your update mechanism
  console.log('Checking for updates...');
  return { updateAvailable: false };
};

export default {
  isNative,
  isAndroid,
  isIOS,
  isWeb,
  initializeApp,
  hapticFeedback,
  vibrate,
  hideKeyboard,
  getAppInfo,
  checkForUpdates
};
